import { useCallback, useEffect, useState } from "react";
import { Upload, Check, X, Trash2, Eye, Loader2, CalendarIcon, Pencil } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { staffApi, errMsg } from "@/lib/api";
import { RecordQrPanel } from "@/components/staff/RecordQrDialog";
import { DevotionalSwitch } from "@/components/staff/DevotionalSwitch";

const AddDevotional = ({ onUploaded }) => {
  const [mode, setMode] = useState("upload");
  return (
    <div className="space-y-3">
      <div className="flex rounded-full bg-white p-1 shadow-sm" data-testid="devotional-add-mode">
        {[["upload", "Upload a video file"], ["record", "Record on iPhone"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setMode(k)} data-testid={`devotional-mode-${k}`}
            className={`flex-1 rounded-full px-4 py-2 text-sm font-medium transition-colors ${mode === k ? "bg-[#0A3A6B] text-white" : "text-slate-600 hover:text-[#0A3A6B]"}`}>{l}</button>
        ))}
      </div>
      {mode === "upload" ? <UploadForm onUploaded={onUploaded} /> : <RecordQrPanel />}
    </div>
  );
};

const STATUS = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-rose-100 text-rose-700", scheduled: "bg-sky-100 text-sky-800" };
const phToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Manila" }).format(new Date());
const shownStatus = (d) => (d.status === "approved" && d.devotional_date > phToday() ? "scheduled" : d.status);
const EMPTY = { title: "", speaker: "", scripture: "", reflection: "", devotional_date: format(new Date(), "yyyy-MM-dd") };

const readMeta = (file) => new Promise((resolve) => {
  const v = document.createElement("video");
  v.preload = "metadata";
  v.onloadedmetadata = () => { resolve({ seconds: v.duration, w: v.videoWidth, h: v.videoHeight }); URL.revokeObjectURL(v.src); };
  v.onerror = () => resolve(null);
  v.src = URL.createObjectURL(file);
});

const pickFile = async (file, setFile) => {
  if (!file) return setFile(null);
  const m = await readMeta(file);
  if (m && m.seconds > 120.5) { setFile(null); return toast.error("Please keep devotionals to 2 minutes or less."); }
  if (m && m.w && m.h <= m.w) { setFile(null); return toast.error("Please record upright (portrait), holding the iPhone vertically."); }
  setFile(file);
};

const DetailFields = ({ f, setF }) => (
  <>
    <Input placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="devotional-title-input" />
    <div className="grid gap-3 sm:grid-cols-2">
      <Input placeholder="Speaker" value={f.speaker} onChange={(e) => setF({ ...f, speaker: e.target.value })} data-testid="devotional-speaker-input" />
      <Popover>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" className="justify-start font-normal" data-testid="devotional-date-button"><CalendarIcon className="mr-2 h-4 w-4" />{format(parseISO(f.devotional_date), "MMM d, yyyy")}</Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto bg-white p-0"><Calendar mode="single" selected={parseISO(f.devotional_date)} onSelect={(d) => d && setF({ ...f, devotional_date: format(d, "yyyy-MM-dd") })} /></PopoverContent>
      </Popover>
    </div>
    <Input placeholder="Scripture (e.g. Psalm 23:1)" value={f.scripture} onChange={(e) => setF({ ...f, scripture: e.target.value })} data-testid="devotional-scripture-input" />
    <Textarea rows={3} placeholder="Short reflection (optional)" value={f.reflection} onChange={(e) => setF({ ...f, reflection: e.target.value })} data-testid="devotional-reflection-input" />
  </>
);

const EditDialog = ({ d, onClose, onSaved }) => {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (d) setF({ title: d.title, speaker: d.speaker, scripture: d.scripture || "", reflection: d.reflection || "", devotional_date: d.devotional_date }); }, [d]);
  const save = async (e) => {
    e.preventDefault();
    if (!f.title.trim() || !f.speaker.trim()) return toast.error("Title and speaker are required");
    setBusy(true);
    try { await staffApi().put(`/devotionals/${d.id}`, f); toast.success("Devotional updated"); onSaved(); onClose(); }
    catch (err) { toast.error(errMsg(err, "Could not save")); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!d} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto bg-white sm:max-w-lg" data-testid="devotional-edit-dialog">
        <DialogTitle>Edit devotional details</DialogTitle>
        {f && (
          <form onSubmit={save} className="space-y-4" data-testid="devotional-edit-form">
            <DetailFields f={f} setF={setF} />
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid="devotional-edit-save-button">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Save changes
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const UploadForm = ({ onUploaded }) => {
  const [f, setF] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [pct, setPct] = useState(0);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !f.title.trim() || !f.speaker.trim()) return toast.error("Title, speaker and video are required");
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    setBusy(true); setPct(0);
    try {
      await staffApi().post("/devotionals", fd, { onUploadProgress: (p) => setPct(Math.round((p.loaded / (p.total || 1)) * 100)) });
      toast.success("Devotional submitted for approval");
      setF(EMPTY); setFile(null); e.target.reset(); onUploaded();
    } catch (err) { toast.error(errMsg(err, "Upload failed")); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4 rounded-3xl border border-slate-200 bg-white p-6" data-testid="devotional-upload-form">
      <h3 className="text-lg font-semibold text-slate-900">Upload a 2-minute devotional</h3>
      <DetailFields f={f} setF={setF} />
      <label className="flex cursor-pointer flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-slate-200 p-6 text-center text-sm text-slate-500 transition-colors hover:border-[#1D64B4] hover:bg-[#F8FAFC]">
        <Upload className="h-6 w-6 text-[#1D64B4]" />
        {file ? <span className="font-medium text-slate-800" data-testid="devotional-file-name">{file.name}</span> : "Choose your iPhone video"}
        <span className="text-xs text-slate-400" data-testid="devotional-video-rules">Recorded on an iPhone · held upright (portrait) · 2 minutes max · up to 300 MB</span>
        <input type="file" accept="video/quicktime,video/mp4,video/*" className="hidden" onChange={(e) => pickFile(e.target.files[0], setFile)} data-testid="devotional-file-input" />
      </label>
      {busy && <Progress value={pct} data-testid="devotional-upload-progress" />}
      <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid="devotional-submit-button">
        {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {pct >= 100 ? "Optimizing video…" : `Uploading ${pct}%`}</> : "Submit for approval"}
      </Button>
    </form>
  );
};

const Row = ({ d, isAdmin, onStatus, onDelete, onPreview, onEdit }) => (
  <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`devotional-row-${d.id}`}>
    <div className="min-w-0 basis-full sm:basis-auto sm:flex-1">
      <p className="truncate font-medium text-slate-900">{d.title}</p>
      <p className="text-xs text-slate-500">{d.speaker} · {format(parseISO(d.devotional_date), "MMM d, yyyy")}{d.duration ? ` · ${Math.floor(d.duration / 60)}:${String(Math.round(d.duration % 60)).padStart(2, "0")}` : ""}{d.device ? ` · ${d.device}` : ""}{d.original_size > d.size ? ` · ${Math.round(d.size / 1048576)} MB (was ${Math.round(d.original_size / 1048576)} MB)` : ""}</p>
    </div>
    <Badge className={`${STATUS[shownStatus(d)]} border-0 ${shownStatus(d) === "scheduled" ? "" : "capitalize"}`} data-testid={`devotional-status-${d.id}`}>{shownStatus(d) === "scheduled" ? `Scheduled · goes live ${format(parseISO(d.devotional_date), "MMM d")}` : d.status}</Badge>
    <Button size="icon" variant="ghost" onClick={() => onPreview(d)} data-testid={`devotional-preview-${d.id}`}><Eye className="h-4 w-4" /></Button>
    {isAdmin && (
      <>
        <Button size="sm" variant="outline" onClick={() => onEdit(d)} data-testid={`devotional-edit-${d.id}`}><Pencil className="mr-1 h-4 w-4" />Edit</Button>
        {d.status !== "approved" && <Button size="sm" onClick={() => onStatus(d, "approved")} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`devotional-approve-${d.id}`}><Check className="mr-1 h-4 w-4" />Approve</Button>}
        {d.status !== "rejected" && <Button size="sm" variant="outline" onClick={() => onStatus(d, "rejected")} data-testid={`devotional-reject-${d.id}`}><X className="mr-1 h-4 w-4" />{d.status === "approved" ? "Unpublish" : "Reject"}</Button>}
        <Button size="icon" variant="ghost" onClick={() => onDelete(d)} className="text-rose-500 hover:text-rose-700" data-testid={`devotional-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
      </>
    )}
  </div>
);

export const DevotionalManager = ({ role }) => {
  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(null);
  const isAdmin = role === "admin";

  const load = useCallback(() => { staffApi().get("/devotionals/manage").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (preview?.url) URL.revokeObjectURL(preview.url); }, [preview]);

  const onStatus = async (d, status) => {
    try { await staffApi().patch(`/devotionals/${d.id}/status`, { status }); toast.success(status === "approved" ? (d.devotional_date > phToday() ? `Scheduled — goes live on ${format(parseISO(d.devotional_date), "MMM d, yyyy")}` : "Published to the website") : "Marked as " + status); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const onDelete = async (d) => {
    if (!window.confirm(`Delete "${d.title}"?`)) return;
    try { await staffApi().delete(`/devotionals/${d.id}`); toast.success("Devotional removed"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const onPreview = async (d) => {
    try {
      toast.loading("Loading preview...", { id: "preview" });
      const r = await staffApi().get(`/devotionals/${d.id}/video`, { responseType: "blob" });
      toast.dismiss("preview");
      setPreview({ title: d.title, url: URL.createObjectURL(r.data) });
    } catch (e) { toast.error("Could not load video", { id: "preview" }); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[420px_1fr]" data-testid="devotional-manager">
      {isAdmin && <div className="lg:col-span-2"><DevotionalSwitch /></div>}
      <AddDevotional onUploaded={load} />
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">All uploads</h3>
          {!isAdmin && <p className="text-xs text-slate-500">An admin approves uploads before they go public.</p>}
        </div>
        {items.length === 0 && <p className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400" data-testid="devotionals-manage-empty">No uploads yet.</p>}
        {items.map((d) => <Row key={d.id} d={d} isAdmin={isAdmin} onStatus={onStatus} onDelete={onDelete} onPreview={onPreview} onEdit={setEditing} />)}
      </div>
      <EditDialog d={editing} onClose={() => setEditing(null)} onSaved={load} />
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-white sm:max-w-sm" data-testid="devotional-preview-dialog">
          <DialogTitle>{preview?.title}</DialogTitle>
          {preview && <video src={preview.url} controls autoPlay className="mx-auto aspect-[9/16] max-h-[75vh] w-full rounded-xl bg-black object-contain" data-testid="devotional-preview-video" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
